import React from 'react';
import { Loader, Unlink } from 'lucide-react';
import { FeatureCard } from '@/components/map/components/FeatureCard';
import { TicketDetail } from '@packages/database/types';
import { useAssetFeature, usePremiseFeature } from '@/hooks/api/useFeatures';
import { findLayerForAsset } from '@/utils/map.utils';

interface LinkedItemSectionProps {
  ticket: TicketDetail;
}

export const LinkedItemSection = ({ ticket }: LinkedItemSectionProps) => {
  return (
    <div className="rounded-lg border bg-muted/20 p-6 space-y-4">
      <h3 className="text-base font-semibold text-foreground pb-2 border-b border-border">
        Linked Item
      </h3>
      <LinkedItemContent ticket={ticket} />
    </div>
  );
};

const LinkedItemContent = ({ ticket }: LinkedItemSectionProps) => {
  if (ticket?.asset) {
    return (
      <AssetFeatureCard
        assetType={ticket.asset.assetType}
        assetId={ticket.asset.id}
      />
    );
  }

  if (ticket?.premise) {
    return <PremiseFeatureCard premiseId={ticket.premise.id} />;
  }

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground italic border rounded-md p-4 bg-background">
      <Unlink className="w-4 h-4 shrink-0" />
      No asset linked to this ticket.
    </div>
  );
};

const AssetFeatureCard = ({
  assetType,
  assetId,
}: {
  assetType: string;
  assetId: string;
}) => {
  const { data, isLoading, isError } = useAssetFeature(assetType, assetId);

  if (isLoading) return <FeatureCardSkeleton />;
  if (isError || !data) return <FeatureCardError />;

  return (
    <FeatureCard
      feature={data.feature}
      layerDef={data.layerDef}
      variant="detailed"
    />
  );
};

const PremiseFeatureCard = ({ premiseId }: { premiseId: string }) => {
  const { data, isLoading, isError } = usePremiseFeature(premiseId);

  if (isLoading) return <FeatureCardSkeleton />;
  if (isError || !data) return <FeatureCardError />;

  return (
    <FeatureCard
      feature={data.feature as any}
      layerDef={data.layerDef}
      variant="detailed"
    />
  );
};

const FeatureCardSkeleton = () => (
  <div className="flex items-center gap-3 animate-pulse">
    <div className="w-8 h-8 rounded-lg bg-muted shrink-0" />
    <div className="space-y-1.5 flex-1">
      <div className="h-3.5 w-32 bg-muted rounded" />
      <div className="h-3 w-20 bg-muted rounded" />
    </div>
  </div>
);

const FeatureCardError = () => (
  <div className="text-xs text-muted-foreground italic p-2">
    Failed to load linked item details.
  </div>
);
